import { GetCartTotalReq, UpsertOrderReq } from "gen/api/order/service_pb"
import {
  AdditionalFees_Fee as AdditionalFeesFee,
  AdditionalFees_FeeOverride as FeeOverride,
  AdditionalFees_Fee_OverrideType as OverrideType,
} from "gen/company/models_pb"
import { Timestamp } from "gen/google/protobuf/timestamp_pb"
import {
  Inventories,
  Product as PBProduct,
  Product_Cost as PBProductCost,
} from "gen/product/models_pb"
import { Address } from "gen/proto/address/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import {
  Order as PBOrder,
  OrderShipment,
  Order_Product as OrderProduct,
  OrderShipment_Product as OrderShipmentProduct,
  OrderShipment_Status as OrderShipmentStatus,
  Order_Status as OrderStatus,
} from "gen/txn/models_pb"

import { Decimal } from "../../classes/decimal"
import { FractionalMoney, Money } from "../../classes/money"
import { UUID } from "../../classes/uuid"
import { Product } from "../../libs/product"
import { Order, orderBaseStatusToPBOrderBaseStatus } from "../../types/orders"

export const productFromOrderProduct = (
  productsById: { [id: string]: PBProduct },
  product: OrderProduct
) => productsById[UUID.fromPB(product.productId!).toString()]

export const getCost = (
  matchedCostProduct?: PBProduct,
  product?: PBProduct,
  vendorId?: PBUUID,
  orderStatus?: OrderStatus,
  uomId?: PBUUID
): PBProductCost | undefined => {
  const costProduct = matchedCostProduct ?? product

  const defaultCost: PBProductCost | undefined =
    costProduct && Product.fromPB(costProduct).defaultCost

  if (
    ![
      OrderStatus.ORDERED,
      OrderStatus.CREATED,
      OrderStatus.UNDEFINED,
      undefined,
    ].includes(orderStatus)
  ) {
    return defaultCost
  }

  return getVendorCost(matchedCostProduct, product, vendorId, uomId)
}

export const getVendorCost = (
  matchedCostProduct?: PBProduct,
  product?: PBProduct,
  vendorId?: PBUUID,
  uomId?: PBUUID
): PBProductCost | undefined => {
  const costProduct = matchedCostProduct ?? product

  const defaultCost: PBProductCost | undefined =
    costProduct && Product.fromPB(costProduct).defaultCost

  if (!costProduct?.costs) {
    return defaultCost
  }

  if (!vendorId) {
    return defaultCost
  }

  const vendorCosts =
    costProduct?.costs
      ?.slice()
      .reverse()
      .filter((cost) => UUID.eqFromPB(vendorId, cost.vendorId)) ?? []
  const vendorCostForUom =
    uomId &&
    vendorCosts.find((cost) => cost.uomId && UUID.eqFromPB(uomId, cost.uomId))
  if (vendorCostForUom) {
    return vendorCostForUom
  }

  const vendorDefaultNoUom = vendorCosts.find(
    (cost) => !cost.uomId && cost.isDefault
  )
  if (vendorDefaultNoUom) {
    return vendorDefaultNoUom
  }

  const vendorCostNoUom = vendorCosts.find((cost) => !cost.uomId)
  return vendorCostNoUom ?? defaultCost
}

export const getProductCostV2 = (
  productsById: { [id: string]: PBProduct },
  orderProduct: OrderProduct,
  vendorId?: PBUUID,
  orderStatus?: OrderStatus
): FractionalMoney => {
  if (orderProduct.costV2) {
    return FractionalMoney.fromPB(orderProduct.costV2)
  }

  const product = productFromOrderProduct(productsById, orderProduct)
  const matchedProduct: PBProduct | undefined = product?.matchedCostProductId
    ? productsById[UUID.fromPB(product?.matchedCostProductId).toString()]
    : undefined

  const productCost: PBProductCost | undefined = getCost(
    matchedProduct,
    product,
    vendorId,
    orderStatus,
    orderProduct.uomId
  )

  return FractionalMoney.fromPB(productCost?.costV2)
}

export interface ProductAdditionalFee {
  name: string
  unitFee: FractionalMoney
  feeId: string
}

export interface OrderFeeOptions {
  orderStatus?: OrderStatus
  locationId?: PBUUID
  costV2?: FractionalMoney
  dropshipAddress?: Address
  locationAddress?: Address
}

export interface PrepareOrderProductOptions
  extends Omit<OrderFeeOptions, "orderStatus" | "costV2"> {
  currentStatus?: OrderStatus
  nextStatus: OrderStatus
  vendorId?: PBUUID
}

// getProductAdditionalFees returns the per-unit additional fees applied to an
// order line at full precision. Reads the multi-fee snapshot list when the
// order is in a terminal state; otherwise computes it live from the company's
// configured additional fee. Returns an empty list when nothing applies.
// Multiplying by quantity before rounding to Money preserves accuracy across
// rollups; rounding here would compound per-line error.
// Keep live fee calculation in sync with ApplyUnitFee and shouldApplyFee in
// rd/company/additional_fees.go.
export const getProductAdditionalFees = (
  additionalFeesByProductId: { [id: string]: AdditionalFeesFee[] },
  orderProduct: OrderProduct,
  {
    orderStatus,
    locationId,
    costV2,
    dropshipAddress,
    locationAddress,
  }: OrderFeeOptions = {}
): ProductAdditionalFee[] => {
  if (orderProduct.additionalFeesWhenReceived?.length) {
    return orderProduct.additionalFeesWhenReceived.map((f) => ({
      name: f.name,
      unitFee: FractionalMoney.fromPB(f.unitFee),
      feeId: f.feeId,
    }))
  }

  if (
    orderStatus === OrderStatus.VOIDED ||
    orderStatus === OrderStatus.RECEIVED
  ) {
    return []
  }

  const productFees =
    additionalFeesByProductId[UUID.fromPB(orderProduct.productId!).toString()]
  if (!productFees) return []

  const cost = costV2 ?? FractionalMoney.fromPB(orderProduct.costV2)
  const state = dropshipAddress?.state || locationAddress?.state
  const locationIdString = locationId && UUID.fromPB(locationId).toString()
  const out: ProductAdditionalFee[] = []
  for (const fee of productFees) {
    if (!fee.applyToOrders) continue

    let override: FeeOverride | undefined
    switch (fee.overrideType) {
      case OverrideType.STATE:
        if (!state) break
        if (!fee.applyToStates[state]) continue
        override = fee.feeOverrideByState[state]
        break
      case OverrideType.LOCATION:
      default:
        if (!locationIdString) break
        if (!fee.applyToLocationIds[locationIdString]) continue
        override = fee.feeOverrideByLocationId[locationIdString]
        break
    }

    const { fee: amount, feeRate } = override ?? fee

    const flatFee = Money.fromPB(amount)
    if (!flatFee.isZero()) {
      out.push({
        name: fee.name,
        unitFee: FractionalMoney.fromMoney(flatFee),
        feeId: fee.idString,
      })
      continue
    }

    const rate = Decimal.fromPBOrZero(feeRate)
    if (rate.isZero()) continue
    out.push({ name: fee.name, unitFee: cost.mult(rate), feeId: fee.idString })
  }
  return out
}

// getProductAdditionalFee returns the summed per-unit additional fee for an
// order line at full precision. Convenience wrapper over
// getProductAdditionalFees for callers that need a single scalar (e.g.
// the cart row's combined `+ $X.XX` display).
export const getProductAdditionalFee = (
  additionalFeesByProductId: { [id: string]: AdditionalFeesFee[] },
  orderProduct: OrderProduct,
  options: OrderFeeOptions = {}
): FractionalMoney =>
  getProductAdditionalFees(
    additionalFeesByProductId,
    orderProduct,
    options
  ).reduce((acc, f) => acc.add(f.unitFee), FractionalMoney.zero())

export const inventoryFromOrderProduct = (
  inventoriesByProductId: Record<string, Inventories>,
  product: OrderProduct
) => inventoriesByProductId[UUID.fromPB(product.productId!).toString()]

export const getNumTotalOrderProducts = (
  products: OrderProduct[],
  orderStatus?: OrderStatus
): string => {
  let quantity = Decimal.zero()
  for (const product of products) {
    if (orderStatus === OrderStatus.RECEIVED) {
      const receivedQuantity = product.quantityReceivedV2
        ? Decimal.fromPB(product.quantityReceivedV2)
        : Decimal.zero()
      quantity = quantity.add(receivedQuantity)
    } else {
      const orderedQuantity = product.quantityOrderedV2
        ? Decimal.fromPB(product.quantityOrderedV2)
        : Decimal.zero()
      quantity = quantity.add(orderedQuantity)
    }
  }
  let productsPluralOrSingular = " products"
  if (quantity.eq(Decimal.fromBigSource(1))) {
    productsPluralOrSingular = " product"
  }
  return quantity.toString() + productsPluralOrSingular
}

export const makeGetOrderCartTotalReq = (
  products: OrderProduct[],
  orderId?: PBUUID,
  vendorId?: PBUUID,
  status?: OrderStatus,
  calculateQuantityOutstanding?: boolean,
  locationId?: PBUUID,
  dropshipAddress?: Address
): GetCartTotalReq => ({
  orderId:
    status === OrderStatus.RECEIVED || status === OrderStatus.VOIDED
      ? orderId
      : undefined,
  locationId,
  address: dropshipAddress,
  vendorId: [undefined, OrderStatus.CREATED, OrderStatus.ORDERED].includes(
    status
  )
    ? vendorId
    : undefined,

  cart: products.map((product) => {
    let quantityV2 = product.quantityOrderedV2 ?? Decimal.zero()
    if (status === OrderStatus.ORDERED && calculateQuantityOutstanding) {
      const outstandingQuantity = getOrderProductQuantityOutstandingV2(product)
      if (!outstandingQuantity) {
        quantityV2 = Decimal.zero()
      } else {
        quantityV2 = outstandingQuantity
      }
    } else if (status === OrderStatus.ORDERED) {
      quantityV2 = product.quantityStagedReceived ?? Decimal.zero()
    } else if (
      status === OrderStatus.RECEIVED ||
      status === OrderStatus.VOIDED
    ) {
      quantityV2 = product.quantityReceivedV2 ?? Decimal.zero()
    }
    return {
      productId: product.productId,
      quantityV2,
      oneTimeCostV2: product.costV2,
      uomId: product.uomId,
    }
  }),
})

export const prepareProductForUpsert = (
  orderProduct: OrderProduct,
  productsById: { [id: string]: PBProduct },
  additionalFeesByProductId: { [id: string]: AdditionalFeesFee[] },
  {
    currentStatus,
    nextStatus,
    vendorId,
    locationId,
    dropshipAddress,
    locationAddress,
  }: PrepareOrderProductOptions
): OrderProduct => {
  const nextIsTerminal =
    nextStatus === OrderStatus.RECEIVED || nextStatus === OrderStatus.VOIDED

  const costV2 = getProductCostV2(productsById || {}, orderProduct, vendorId)

  return {
    ...orderProduct,
    quantityReceivedV2: !nextIsTerminal
      ? Decimal.zero()
      : currentStatus === OrderStatus.ORDERED
        ? orderProduct.quantityReceivedV2
        : orderProduct.quantityOrderedV2,
    quantityStagedReceived:
      currentStatus === OrderStatus.ORDERED
        ? orderProduct.quantityStagedReceived
        : undefined,
    costV2: orderProduct.costV2 ?? costV2,
    additionalFeesWhenReceived: nextIsTerminal
      ? getProductAdditionalFees(
          additionalFeesByProductId ?? {},
          orderProduct,
          { locationId, costV2, dropshipAddress, locationAddress }
        ).map((f) => ({
          name: f.name,
          unitFee: f.unitFee,
          feeId: f.feeId,
        }))
      : [],
  }
}

export const getOrderRoute = (status: OrderStatus | undefined): string =>
  status === OrderStatus.CREATED || status === undefined
    ? "drafts"
    : OrderStatus[status].toLowerCase()

export const getOrderShipmentRoute = (
  status: OrderShipmentStatus | undefined
): string =>
  status === OrderShipmentStatus.UNDEFINED || status === undefined
    ? ""
    : OrderShipmentStatus[status].toLowerCase()

export const getOrderProductSubtotalV2 = (
  productsById: { [id: string]: PBProduct },
  product: OrderProduct,
  status?: OrderStatus,
  vendorId?: PBUUID,
  calculateQuantityOutstanding?: boolean
): Money => {
  const costV2: FractionalMoney = getProductCostV2(
    productsById,
    product,
    vendorId,
    status
  )

  if (!status || status === OrderStatus.CREATED) {
    const orderedQuantity = product.quantityOrderedV2
      ? Decimal.fromPB(product.quantityOrderedV2)
      : Decimal.zero()
    return costV2.mult(orderedQuantity).toMoney()
  } else if (status === OrderStatus.ORDERED && calculateQuantityOutstanding) {
    const outstandingQuantity = getOrderProductQuantityOutstandingV2(product)
    if (!outstandingQuantity) return Money.zero()
    return costV2.mult(outstandingQuantity).toMoney()
  } else if (status === OrderStatus.ORDERED) {
    const stagedQuantity = product.quantityStagedReceived
      ? Decimal.fromPB(product.quantityStagedReceived)
      : Decimal.zero()
    return costV2.mult(stagedQuantity).toMoney()
  }
  const receivedQuantity = product.quantityReceivedV2
    ? Decimal.fromPB(product.quantityReceivedV2)
    : Decimal.zero()
  return costV2.mult(receivedQuantity).toMoney()
}

export const getRemainingOrderedQuantity = (
  orderedQuantity: Decimal,
  receivedQuantity: Decimal
): Decimal => {
  if (orderedQuantity.lt(Decimal.zero())) {
    return orderedQuantity.sub(receivedQuantity)
  }
  return Decimal.max(orderedQuantity.sub(receivedQuantity), Decimal.zero())
}

export const getOrderProductQuantityOutstandingV2 = (product: OrderProduct) => {
  const orderedQuantity = product.quantityOrderedV2
    ? Decimal.fromPB(product.quantityOrderedV2)
    : Decimal.zero()
  const receivedQuantity = product.quantityReceivedV2
    ? Decimal.fromPB(product.quantityReceivedV2)
    : Decimal.zero()
  return getRemainingOrderedQuantity(orderedQuantity, receivedQuantity)
}

export const prepareOrderForUpsert = (
  order: Order,
  updatedProducts?: OrderProduct[],
  nextStatus?: OrderStatus
): UpsertOrderReq => {
  const currentProducts = updatedProducts ?? order.productsPB
  const productPBStatus = orderBaseStatusToPBOrderBaseStatus[order.status]
  const preparedProducts = currentProducts.map((product) =>
    prepareProductForUpsert(
      product,
      order.productsById,
      order.additionalFeesByProductId,
      {
        currentStatus: productPBStatus,
        nextStatus: nextStatus ?? productPBStatus,
        vendorId: order.vendorId,
        locationId: order.locationId,
        dropshipAddress: order.dropshipAddressPB,
        locationAddress: order.locationAddressPB,
      }
    )
  )
  const req: UpsertOrderReq = {
    id: order.id,
    products: preparedProducts,
    status: nextStatus ?? productPBStatus,
    vendorId: order.vendorId,
    locationId: order.locationId,
    requestedDeliveryAt: order.requestedDeliveryAt
      ? Timestamp.fromDate(order.requestedDeliveryAt)
      : undefined,
    notes: order.orderNotes ?? "",
    dropshipAddress: order.dropshipAddressPB,
    dropshipRecipient: order.dropshipRecipient ?? "",
    method: order.orderMethodPB,
    promoCode: order.promoCode ?? "",
    voidReason: "",
    costAdjustments: order.costAdjustmentsPB,
  }
  return req
}

export function processOrderProducts(
  orderProducts: OrderProduct[] | OrderShipmentProduct[],
  productsById: { [key: string]: PBProduct } = {},
  matchedProductsById: { [key: string]: PBProduct } = {},
  status?: OrderStatus
) {
  const products = [...orderProducts]
    .sort((a, b) => a.position - b.position)
    .filter((prod) => prod.productId)
    .map((orderProduct) => {
      const fullProduct = orderProduct.productId
        ? productsById[UUID.fromPB(orderProduct.productId).toString()]
        : undefined
      const matchedProduct = fullProduct?.matchedCostProductId
        ? matchedProductsById[
            UUID.fromPB(fullProduct.matchedCostProductId).toString()
          ]
        : undefined

      let defaultCostV2: FractionalMoney
      if (matchedProduct) {
        defaultCostV2 = FractionalMoney.fromPB(
          Product.fromPB(matchedProduct).defaultCost?.costV2
        )
      } else if (fullProduct) {
        defaultCostV2 = FractionalMoney.fromPB(
          Product.fromPB(fullProduct).defaultCost?.costV2
        )
      } else {
        defaultCostV2 = FractionalMoney.zero()
      }

      const quantityOrderedV2 =
        "quantityOrderedV2" in orderProduct && orderProduct.quantityOrderedV2
          ? Decimal.fromPB(orderProduct.quantityOrderedV2)
          : Decimal.zero()

      const quantityReceivedV2 = orderProduct.quantityReceivedV2
        ? Decimal.fromPB(orderProduct.quantityReceivedV2)
        : Decimal.zero()

      return {
        id: orderProduct.productId,
        quantityOrderedV2: quantityOrderedV2,
        quantityReceivedV2: quantityReceivedV2,
        quantityOutstandingV2: Decimal.max(
          quantityOrderedV2.sub(quantityReceivedV2),
          Decimal.zero()
        ),
        identifier: fullProduct?.identifiers[0],
        name: fullProduct?.name,
        defaultCostV2:
          (status === OrderStatus.RECEIVED || status === OrderStatus.VOIDED) &&
          orderProduct.costV2
            ? FractionalMoney.fromPB(orderProduct.costV2)
            : defaultCostV2,
      }
    })

  let totalOrdered = Decimal.zero()
  let totalReceived = Decimal.zero()
  let totalOutstanding = Decimal.zero()
  for (const prod of products) {
    totalOrdered = totalOrdered.add(prod.quantityOrderedV2)
    totalReceived = totalReceived.add(prod.quantityReceivedV2)
    totalOutstanding = totalOutstanding.add(prod.quantityOutstandingV2)
  }

  const totalQuantity =
    status === OrderStatus.RECEIVED ? totalReceived : totalOrdered
  const skuCount = products.length
  const skuAndUnitSummary = `${skuCount} ${skuCount === 1 ? "SKU" : "SKUs"} · ${totalQuantity.toString()} ${
    totalQuantity.eq(Decimal.fromBigSource(1)) ? "unit" : "units"
  }`

  return {
    products,
    skuAndUnitSummary,
    totalReceived,
    totalOutstanding,
  }
}

export function convertStatusToEnglish(status?: OrderStatus) {
  switch (status) {
    case OrderStatus.CREATED:
      return "Added to order"
    case OrderStatus.ORDERED:
      return "Ordered"
    case OrderStatus.RECEIVED:
      return "Received"
    case OrderStatus.VOIDED:
      return "Voided"
    default:
      return "Needs order"
  }
}

export function isOrderBackordered(
  order?: PBOrder,
  orderShipments?: OrderShipment[]
) {
  return order?.status === OrderStatus.ORDERED && !!orderShipments?.length
}
